import React from "react";
import errorIcon from "../../components/customtoast/icons/error.png";
import { LoaderIcon } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import { getDeviceFingerprint, getDeviceToken } from "../../utils/deviceIdentity";
import { startSurveySession } from "../../utils/surveySession";

// Culture audit focus groups: participants open the pipeline link (or scan its
// QR code) and go straight into the survey, anonymously — no access code, no
// name or email. The backend recognises a returning device by its token (see
// utils/deviceIdentity.ts) or enrols it as a new participant.
function PipelineJoin(): JSX.Element {
  const navigate = useNavigate();
  const { pipeline_id: pipelineId }: any = useParams();
  const attempted = React.useRef(false);
  const [error, setError] = React.useState<string | null>(null);

  const join = async () => {
    setError(null);
    try {
      const device_fingerprint = await getDeviceFingerprint();
      const data = await api.surveyPipelineLogin({
        pipeline_id: pipelineId,
        device_token: getDeviceToken(),
        ...(device_fingerprint && { device_fingerprint }),
      });

      await startSurveySession(data?.data, data?.data?.access_code || null);
      navigate("/candidate/self-review", { replace: true });
    } catch (err: any) {
      // No response at all means the server wasn't reached, not a bad link.
      setError(
        !err?.response
          ? "We couldn't connect to the survey. Please check your internet connection and try again."
          : err.response.data?.message || "This link is not open. Please check with your facilitator."
      );
    }
  };

  // Once per mount — StrictMode's double effect would otherwise send two
  // requests (the backend copes, but there's no need).
  React.useEffect(() => {
    if (!attempted.current) {
      attempted.current = true;
      join();
    }
  }, []);

  // Same neutral screens as the access-code link (CultureAuditLogin.tsx): the
  // client isn't known until the backend answers, so no TestAssessify branding.
  if (error) {
    return (
      <div className="bg-white min-h-screen p-6 flex justify-center items-center">
        <div
          role="alertdialog"
          aria-labelledby="join-error-title"
          aria-describedby="join-error-message"
          className="flex flex-col items-center justify-center gap-2 rounded-lg w-full mx-auto p-7 bg-white shadow-2xl"
          style={{ maxWidth: "500px" }}
        >
          <div className="w-full p-2 flex items-center justify-center">
            <img src={errorIcon} alt="" className="w-36 h-36" />
          </div>
          <h1 id="join-error-title" className="text-center text-slate-700 font-normal text-2xl">
            Error!
          </h1>
          <p id="join-error-message" className="text-center text-slate-600">
            {error}
          </p>
          <button
            type="button"
            onClick={join}
            className="w-full mt-4 rounded-md text-baseFont bg-customOrange text-white py-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-screen flex flex-col items-center justify-center">
      <LoaderIcon />
      <p className="mt-4 text-slate-500">Loading your survey...</p>
    </div>
  );
}

export default PipelineJoin;
